/**
 * LP-secties (landingspagina) – interactieve componenten.
 * Wordt als ES-module geladen, dus maar één keer per pagina uitgevoerd,
 * ook als meerdere secties het script opnemen.
 */

const lpIsDesignMode = () => Boolean(window.Shopify && window.Shopify.designMode);
const lpReducedMotion = () => window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;

/* ------------------------------------------------------------------------ */
/* Geldnotatie: afgeleid van een door Liquid (`money`) gerenderd voorbeeld,  */
/* zodat JS exact dezelfde notatie gebruikt als de server (ook bij Markets). */
/* ------------------------------------------------------------------------ */

function lpDecodeEntities(value) {
  const textarea = document.createElement('textarea');
  textarea.innerHTML = String(value ?? '');
  return textarea.value;
}

function lpMoneyFormatter(sample) {
  const text = lpDecodeEntities(sample).replace(/<[^>]*>/g, '').trim();
  const first = text.search(/\d/);
  let last = -1;
  for (let i = text.length - 1; i >= 0; i -= 1) {
    if (/\d/.test(text[i])) {
      last = i;
      break;
    }
  }

  if (first < 0 || last < 0) {
    return (cents) => (Number(cents || 0) / 100).toFixed(2);
  }

  const prefix = text.slice(0, first);
  const suffix = text.slice(last + 1);
  const core = text.slice(first, last + 1);
  const digits = core.replace(/\D/g, '');
  const decimals = Math.max(0, digits.length - 5);
  const decimalSeparator = decimals > 0 ? core.charAt(core.length - decimals - 1) : '';
  const integerPart = decimals > 0 ? core.slice(0, core.length - decimals - 1) : core;
  const thousandsMatch = integerPart.match(/^\d+(\D+)\d/);
  const thousandsSeparator = thousandsMatch ? thousandsMatch[1] : '';

  return (cents) => {
    const amount = Number(cents || 0);
    const negative = amount < 0;
    const value = Math.abs(amount) / 100;
    const fixed = decimals > 0 ? value.toFixed(decimals) : String(Math.round(value));
    const [whole, fraction] = fixed.split('.');
    const grouped = whole.replace(/\B(?=(\d{3})+(?!\d))/g, thousandsSeparator);
    return `${negative ? '-' : ''}${prefix}${grouped}${decimals > 0 ? decimalSeparator + fraction : ''}${suffix}`;
  };
}

/* ------------------------------------------------------------------------ */
/* Productformulier: varianten, bundels (hoeveelheidskorting), abonnement,   */
/* cadeaus en de sticky balk. Het echte toevoegen aan de winkelwagen doet    */
/* Horizon's <product-form-component> (cart drawer, animatie, foutmeldingen) */
/* ------------------------------------------------------------------------ */

class LpProductForm extends HTMLElement {
  connectedCallback() {
    const jsonEl = this.querySelector('script[data-lp-product-json]');
    try {
      this.product = jsonEl ? JSON.parse(jsonEl.textContent) : null;
    } catch (error) {
      this.product = null;
    }

    if (!this.product || !Array.isArray(this.product.variants)) return;

    this.money = lpMoneyFormatter(this.dataset.moneySample);
    this.section = this.closest('.shopify-section') || this.parentElement;
    this.form = this.querySelector('form[data-type="add-to-cart-form"]');
    this.productFormComponent = this.querySelector('product-form-component');

    const initialId = Number(this.dataset.variantId);
    this.variant = this.product.variants.find((variant) => variant.id === initialId) || this.product.variants[0];

    this.onChange = this.onChange.bind(this);
    this.onSubmit = this.onSubmit.bind(this);
    this.addEventListener('change', this.onChange);
    this.form?.addEventListener('submit', this.onSubmit, true);

    this.syncOptionInputs();
    this.update();
  }

  disconnectedCallback() {
    this.removeEventListener('change', this.onChange);
    this.form?.removeEventListener('submit', this.onSubmit, true);
  }

  get bundleElements() {
    return Array.from(this.querySelectorAll('[data-lp-bundle]'));
  }

  selectedBundle() {
    const bundles = this.bundleElements;
    if (!bundles.length) return null;
    const checked = bundles.find((bundle) => bundle.querySelector('input[type="radio"]')?.checked) || bundles[0];
    return {
      element: checked,
      quantity: Math.max(1, parseInt(checked.dataset.quantity, 10) || 1),
      discount: Math.min(95, Math.max(0, parseInt(checked.dataset.discount, 10) || 0)),
      code: (checked.dataset.code || '').trim(),
    };
  }

  selectedSellingPlanId() {
    const toggle = this.querySelector('input[data-lp-subscription]');
    if (!toggle || !toggle.checked) return null;
    return toggle.value || null;
  }

  basePrice(variant) {
    const planId = this.selectedSellingPlanId();
    if (planId && Array.isArray(variant.selling_plan_allocations)) {
      const allocation = variant.selling_plan_allocations.find((item) => String(item.selling_plan_id) === String(planId));
      if (allocation && typeof allocation.price === 'number') return allocation.price;
    }
    return variant.price;
  }

  numbers(variant, quantity, discount) {
    const base = this.basePrice(variant);
    const unit = Math.floor((base * (100 - discount)) / 100);
    const compareUnit =
      variant.compare_at_price && variant.compare_at_price > variant.price ? variant.compare_at_price : variant.price;
    const total = unit * quantity;
    const compareTotal = compareUnit * quantity;
    const percent = compareUnit > 0 ? Math.round(((compareUnit - unit) * 100) / compareUnit) : 0;
    return { unit, compareUnit, total, compareTotal, percent, saving: compareTotal - total, quantity };
  }

  fill(template, numbers) {
    return String(template || '')
      .split('[percent]').join(String(numbers.percent))
      .split('[total]').join(this.money(numbers.total))
      .split('[compare_total]').join(this.money(numbers.compareTotal))
      .split('[unit_price]').join(this.money(numbers.unit))
      .split('[compare_price]').join(this.money(numbers.compareUnit))
      .split('[saving]').join(this.money(numbers.saving))
      .split('[quantity]').join(String(numbers.quantity));
  }

  onChange(event) {
    const target = event.target;
    if (!(target instanceof HTMLElement)) return;

    if (target.matches('[data-lp-option]')) {
      this.onOptionChange();
      return;
    }

    if (target.matches('input[data-lp-bundle-input], input[data-lp-subscription]')) {
      this.update();
    }
  }

  currentOptionValues() {
    const values = [];
    this.querySelectorAll('[data-lp-option-group]').forEach((group) => {
      const position = parseInt(group.dataset.position, 10) - 1;
      const select = group.querySelector('select[data-lp-option]');
      if (select) {
        values[position] = select.value;
        return;
      }
      const checked = group.querySelector('input[data-lp-option]:checked');
      if (checked) values[position] = checked.value;
    });
    return values;
  }

  onOptionChange() {
    const values = this.currentOptionValues();
    const match = this.product.variants.find((variant) =>
      variant.options.every((value, index) => values[index] === undefined || values[index] === value)
    );

    if (match) {
      this.variant = match;
      this.dataset.unavailable = 'false';
    } else {
      this.dataset.unavailable = 'true';
    }

    this.syncOptionInputs();
    this.update();

    if (match) {
      this.updateUrl(match);
      this.syncGallery(match);
    }
  }

  syncOptionInputs() {
    const values = this.variant ? this.variant.options : [];
    this.querySelectorAll('[data-lp-option-group]').forEach((group) => {
      const position = parseInt(group.dataset.position, 10) - 1;
      const selectedLabel = group.querySelector('[data-lp-option-selected]');
      const current = this.currentOptionValues();
      if (selectedLabel) selectedLabel.textContent = current[position] ?? values[position] ?? '';

      group.querySelectorAll('input[data-lp-option]').forEach((input) => {
        const probe = current.slice();
        probe[position] = input.value;
        const available = this.product.variants.some(
          (variant) => variant.available && variant.options.every((value, index) => probe[index] === undefined || probe[index] === value)
        );
        input.closest('.lp-option')?.classList.toggle('is-unavailable', !available);
      });
    });
  }

  updateUrl(variant) {
    if (lpIsDesignMode() || !window.history?.replaceState) return;
    try {
      const url = new URL(window.location.href);
      url.searchParams.set('variant', variant.id);
      window.history.replaceState(window.history.state, '', url.toString());
    } catch (error) {
      /* niets */
    }
  }

  syncGallery(variant) {
    const mediaId = variant.featured_media && variant.featured_media.id;
    if (!mediaId || !this.section) return;
    const gallery = this.section.querySelector('kr-product-gallery');
    if (!gallery || typeof gallery.select !== 'function') return;
    const slides = Array.from(gallery.querySelectorAll('[data-kr-slide]'));
    const index = slides.findIndex((slide) => slide.dataset.mediaId === String(mediaId));
    if (index >= 0) gallery.select(index);
  }

  setButtonLabel(button, label) {
    if (!button || !label) return;
    const textNode = Array.from(button.childNodes).find((node) => node.nodeType === Node.TEXT_NODE && node.textContent.trim());
    if (textNode) {
      textNode.textContent = label;
    }
  }

  update() {
    const variant = this.variant;
    if (!variant) return;

    const unavailable = this.dataset.unavailable === 'true';
    const available = variant.available && !unavailable;
    const bundle = this.selectedBundle();
    const quantity = bundle ? bundle.quantity : 1;
    const planId = this.selectedSellingPlanId();

    /* Formulier voor Horizon's product-form-component */
    if (this.form) {
      const idInput = this.form.querySelector('input[name="id"]');
      if (idInput) {
        idInput.value = String(variant.id);
        idInput.disabled = !available;
      }
      const quantityInput = this.form.querySelector('input[name="quantity"]');
      if (quantityInput) quantityInput.value = String(quantity);
      const planInput = this.form.querySelector('input[name="selling_plan"]');
      if (planInput) {
        planInput.value = planId || '';
        planInput.disabled = !planId;
      }
    }
    if (this.productFormComponent) this.productFormComponent.dataset.quantityDefault = String(quantity);

    /* Bundels */
    this.bundleElements.forEach((element) => {
      const bundleQuantity = Math.max(1, parseInt(element.dataset.quantity, 10) || 1);
      const bundleDiscount = Math.min(95, Math.max(0, parseInt(element.dataset.discount, 10) || 0));
      const numbers = this.numbers(variant, bundleQuantity, bundleDiscount);
      const isSelected = bundle && bundle.element === element;
      element.classList.toggle('is-selected', Boolean(isSelected));

      element.querySelectorAll('[data-lp-money]').forEach((node) => {
        const key = node.dataset.lpMoney;
        if (key in numbers) node.textContent = this.money(numbers[key]);
      });
      element.querySelectorAll('[data-lp-compare]').forEach((node) => {
        node.hidden = !(numbers.compareUnit > numbers.unit);
      });
      element.querySelectorAll('[data-lp-template]').forEach((node) => {
        const text = this.fill(node.dataset.lpTemplate, numbers);
        node.textContent = text;
        const hideWhenNoSaving = node.hasAttribute('data-lp-needs-saving');
        node.hidden = !text.trim() || (hideWhenNoSaving && numbers.percent <= 0);
      });
    });

    /* Losse prijsblokken */
    const priceNumbers = this.numbers(variant, 1, bundle ? bundle.discount : 0);
    this.querySelectorAll('[data-lp-price-block]').forEach((block) => {
      const current = block.querySelector('[data-lp-price-current]');
      const compare = block.querySelector('[data-lp-price-compare]');
      const badge = block.querySelector('[data-lp-price-badge]');
      if (current) current.textContent = this.money(priceNumbers.unit);
      if (compare) {
        compare.textContent = this.money(priceNumbers.compareUnit);
        compare.hidden = !(priceNumbers.compareUnit > priceNumbers.unit);
      }
      if (badge) {
        badge.textContent = this.fill(badge.dataset.lpTemplate, priceNumbers);
        badge.hidden = priceNumbers.percent <= 0 || !badge.textContent.trim();
      }
    });

    /* Abonnement */
    this.querySelectorAll('[data-lp-subscription-card]').forEach((card) => {
      const toggle = card.querySelector('input[data-lp-subscription]');
      card.classList.toggle('is-selected', Boolean(toggle && toggle.checked));
    });

    /* Cadeaus */
    this.querySelectorAll('[data-lp-gift]').forEach((gift) => {
      const needed = Math.max(1, parseInt(gift.dataset.unlock, 10) || 1);
      const unlocked = quantity >= needed;
      gift.classList.toggle('is-unlocked', unlocked);
      gift.classList.toggle('is-locked', !unlocked);
    });

    /* Knoppen */
    const addButton = this.form?.querySelector('button[name="add"]');
    if (addButton) {
      addButton.disabled = !available;
      this.setButtonLabel(addButton, available ? this.dataset.addLabel : this.dataset.soldOutLabel);
    }

    const totalNumbers = bundle ? this.numbers(variant, bundle.quantity, bundle.discount) : this.numbers(variant, 1, 0);
    document.querySelectorAll(`[data-lp-sticky-for="${this.dataset.sectionId}"]`).forEach((bar) => {
      const price = bar.querySelector('[data-lp-sticky-price]');
      if (price) price.textContent = this.money(totalNumbers.total);
      const compare = bar.querySelector('[data-lp-sticky-compare]');
      if (compare) {
        compare.textContent = this.money(totalNumbers.compareTotal);
        compare.hidden = !(totalNumbers.compareTotal > totalNumbers.total);
      }
      const stickyButton = bar.querySelector('[data-lp-sticky-submit]');
      if (stickyButton) {
        stickyButton.disabled = !available;
        stickyButton.textContent = available ? this.dataset.addLabel : this.dataset.soldOutLabel;
      }
    });
  }

  onSubmit() {
    const bundle = this.selectedBundle();
    if (!bundle || !bundle.code) return;
    const root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
    const url = `${root}discount/${encodeURIComponent(bundle.code)}?redirect=${encodeURIComponent(`${root}cart.js`)}`;
    fetch(url, { credentials: 'same-origin' }).catch(() => {
      /* Kortingscode kon niet worden toegepast; de bestelling gaat gewoon door. */
    });
  }
}

/* ------------------------------------------------------------------------ */
/* Sticky koopbalk: verschijnt zodra de echte knop boven uit beeld is.      */
/* Werkt met elke scrollcontainer (ook Horizon's .page-wrapper) en ook bij  */
/* snelle sprongen (ankerlinks), omdat de positie bij elke scroll gemeten   */
/* wordt in plaats van alleen bij intersectie-overgangen.                   */
/* ------------------------------------------------------------------------ */

class LpStickyBar extends HTMLElement {
  connectedCallback() {
    this.target = this.dataset.target ? document.querySelector(this.dataset.target) : null;
    if (!this.target) return;
    this.button = this.querySelector('[data-lp-sticky-submit]');
    this.onClick = () => {
      const realButton = this.target.querySelector('button[name="add"], button[type="submit"]');
      if (realButton && !realButton.disabled) realButton.click();
    };
    this.frame = 0;
    this.onScroll = () => {
      if (this.frame) return;
      this.frame = requestAnimationFrame(() => {
        this.frame = 0;
        this.update();
      });
    };
    this.button?.addEventListener('click', this.onClick);
    document.addEventListener('scroll', this.onScroll, { capture: true, passive: true });
    window.addEventListener('resize', this.onScroll, { passive: true });
    this.update();
  }

  disconnectedCallback() {
    this.button?.removeEventListener('click', this.onClick);
    document.removeEventListener('scroll', this.onScroll, { capture: true });
    window.removeEventListener('resize', this.onScroll);
    if (this.frame) cancelAnimationFrame(this.frame);
  }

  update() {
    if (!this.target || !this.target.isConnected) return;
    const visible = this.target.getBoundingClientRect().bottom < 0;
    this.dataset.visible = visible ? 'true' : 'false';
    this.inert = !visible;
  }
}

/* ------------------------------------------------------------------------ */
/* Tabbladen                                                                */
/* ------------------------------------------------------------------------ */

class LpTabs extends HTMLElement {
  connectedCallback() {
    this.tabs = Array.from(this.querySelectorAll('[role="tab"]'));
    this.panels = Array.from(this.querySelectorAll('[role="tabpanel"]'));
    this.tabs.forEach((tab, index) => {
      tab.addEventListener('click', () => this.select(index));
      tab.addEventListener('keydown', (event) => this.onKeydown(event, index));
    });
    this.onBlockSelect = (event) => {
      const index = this.panels.indexOf(event.target.closest('[role="tabpanel"]'));
      if (index >= 0) this.select(index);
    };
    this.addEventListener('shopify:block:select', this.onBlockSelect);
  }

  disconnectedCallback() {
    this.removeEventListener('shopify:block:select', this.onBlockSelect);
  }

  select(index, focus = false) {
    this.tabs.forEach((tab, i) => {
      const active = i === index;
      tab.setAttribute('aria-selected', active ? 'true' : 'false');
      tab.tabIndex = active ? 0 : -1;
      tab.classList.toggle('is-active', active);
      if (active && focus) tab.focus();
    });
    this.panels.forEach((panel, i) => {
      panel.hidden = i !== index;
    });
  }

  onKeydown(event, index) {
    const count = this.tabs.length;
    let next = null;
    if (event.key === 'ArrowRight' || event.key === 'ArrowDown') next = (index + 1) % count;
    if (event.key === 'ArrowLeft' || event.key === 'ArrowUp') next = (index - 1 + count) % count;
    if (event.key === 'Home') next = 0;
    if (event.key === 'End') next = count - 1;
    if (next !== null) {
      event.preventDefault();
      this.select(next, true);
    }
  }
}

/* ------------------------------------------------------------------------ */
/* Carrousel (scroll-snap + pijlen)                                         */
/* ------------------------------------------------------------------------ */

class LpCarousel extends HTMLElement {
  connectedCallback() {
    this.track = this.querySelector('[data-lp-track]');
    if (!this.track) return;
    this.prevButton = this.querySelector('[data-lp-prev]');
    this.nextButton = this.querySelector('[data-lp-next]');
    this.onPrev = () => this.go(-1);
    this.onNext = () => this.go(1);
    this.onScroll = () => this.updateButtons();
    this.prevButton?.addEventListener('click', this.onPrev);
    this.nextButton?.addEventListener('click', this.onNext);
    this.track.addEventListener('scroll', this.onScroll, { passive: true });
    if ('ResizeObserver' in window) {
      this.resizeObserver = new ResizeObserver(() => this.updateButtons());
      this.resizeObserver.observe(this.track);
    }
    this.onBlockSelect = (event) => {
      const slide = event.target.closest('[data-lp-slide]');
      if (slide) this.track.scrollTo({ left: slide.offsetLeft - this.track.offsetLeft, behavior: 'smooth' });
    };
    this.addEventListener('shopify:block:select', this.onBlockSelect);
    this.updateButtons();
  }

  disconnectedCallback() {
    this.resizeObserver?.disconnect();
    this.removeEventListener('shopify:block:select', this.onBlockSelect);
  }

  step() {
    const slide = this.track.querySelector('[data-lp-slide]');
    if (!slide) return this.track.clientWidth;
    const style = window.getComputedStyle(this.track);
    const gap = parseFloat(style.columnGap || style.gap) || 0;
    return slide.getBoundingClientRect().width + gap;
  }

  go(direction) {
    this.track.scrollBy({ left: direction * this.step(), behavior: lpReducedMotion() ? 'auto' : 'smooth' });
  }

  updateButtons() {
    const max = this.track.scrollWidth - this.track.clientWidth - 2;
    const atStart = this.track.scrollLeft <= 2;
    const atEnd = this.track.scrollLeft >= max;
    if (this.prevButton) this.prevButton.disabled = atStart;
    if (this.nextButton) this.nextButton.disabled = atEnd;
    this.toggleAttribute('data-scrollable', max > 2);
  }
}

/* ------------------------------------------------------------------------ */
/* Tellers (0% -> 96%)                                                      */
/* ------------------------------------------------------------------------ */

class LpCounter extends HTMLElement {
  connectedCallback() {
    const raw = String(this.dataset.target || this.textContent || '0').trim();
    const separator = raw.includes(',') ? ',' : '.';
    const normalized = raw.replace(',', '.');
    const target = parseFloat(normalized);
    if (Number.isNaN(target)) return;

    const decimals = (normalized.split('.')[1] || '').length;
    const duration = parseInt(this.dataset.duration, 10) || 1600;
    const format = (value) => value.toFixed(decimals).replace('.', separator);

    if (lpIsDesignMode() || lpReducedMotion() || !('IntersectionObserver' in window)) {
      this.textContent = format(target);
      return;
    }

    this.textContent = format(0);
    this.observer = new IntersectionObserver(
      (entries) => {
        if (!entries.some((entry) => entry.isIntersecting)) return;
        this.observer.disconnect();
        const start = performance.now();
        const tick = (now) => {
          const progress = Math.min(1, (now - start) / duration);
          const eased = 1 - Math.pow(1 - progress, 3);
          this.textContent = format(target * eased);
          if (progress < 1) requestAnimationFrame(tick);
        };
        requestAnimationFrame(tick);
      },
      { threshold: 0.4 }
    );
    this.observer.observe(this);
  }

  disconnectedCallback() {
    this.observer?.disconnect();
  }
}

/* ------------------------------------------------------------------------ */
/* Doorlopende band (marquee)                                               */
/* ------------------------------------------------------------------------ */

class LpMarquee extends HTMLElement {
  connectedCallback() {
    this.track = this.querySelector('[data-lp-marquee-track]');
    this.group = this.querySelector('[data-lp-marquee-group]');
    if (!this.track || !this.group) return;
    this.lastWidth = 0;
    this.build();
    if ('ResizeObserver' in window) {
      this.resizeObserver = new ResizeObserver(() => {
        if (Math.abs(this.clientWidth - this.lastWidth) > 4) this.build();
      });
      this.resizeObserver.observe(this);
    }
  }

  disconnectedCallback() {
    this.resizeObserver?.disconnect();
  }

  build() {
    this.lastWidth = this.clientWidth;
    this.track.querySelectorAll('[data-lp-clone]').forEach((node) => node.remove());
    const groupWidth = this.group.getBoundingClientRect().width;
    if (!groupWidth) return;
    const copies = Math.min(12, Math.ceil((this.clientWidth * 1.5) / groupWidth) + 1);
    for (let i = 0; i < copies; i += 1) {
      const clone = this.group.cloneNode(true);
      clone.removeAttribute('data-lp-marquee-group');
      clone.setAttribute('aria-hidden', 'true');
      clone.setAttribute('data-lp-clone', '');
      clone.querySelectorAll('[data-shopify-editor-block]').forEach((node) => node.removeAttribute('data-shopify-editor-block'));
      this.track.appendChild(clone);
    }
    const speed = Math.max(5, parseInt(this.dataset.speed, 10) || 40);
    this.style.setProperty('--lp-marquee-distance', `${groupWidth}px`);
    this.style.setProperty('--lp-marquee-duration', `${(groupWidth / speed).toFixed(2)}s`);
    this.setAttribute('data-ready', '');
  }
}

/* ------------------------------------------------------------------------ */
/* Popup (dialog)                                                           */
/* ------------------------------------------------------------------------ */

class LpModalOpener extends HTMLElement {
  connectedCallback() {
    this.button = this.querySelector('button');
    this.dialog = this.querySelector('dialog');
    if (!this.button || !this.dialog) return;
    this.onOpen = () => {
      if (typeof this.dialog.showModal === 'function') {
        this.dialog.showModal();
      } else {
        this.dialog.setAttribute('open', '');
      }
    };
    this.onClick = (event) => {
      if (event.target === this.dialog || event.target.closest('[data-lp-modal-close]')) this.dialog.close?.();
    };
    this.button.addEventListener('click', this.onOpen);
    this.dialog.addEventListener('click', this.onClick);
  }

  disconnectedCallback() {
    this.button?.removeEventListener('click', this.onOpen);
    this.dialog?.removeEventListener('click', this.onClick);
  }
}

/* ------------------------------------------------------------------------ */
/* Accordeon-groep (optioneel: maar één tegelijk open)                      */
/* ------------------------------------------------------------------------ */

class LpAccordionGroup extends HTMLElement {
  connectedCallback() {
    this.onToggle = (event) => {
      const details = event.target;
      if (!(details instanceof HTMLDetailsElement) || !details.open) return;
      if (!this.hasAttribute('data-exclusive')) return;
      this.querySelectorAll('details[open]').forEach((other) => {
        if (other !== details) other.open = false;
      });
    };
    this.onBlockSelect = (event) => {
      const details = event.target.closest('details');
      if (details) details.open = true;
    };
    this.addEventListener('toggle', this.onToggle, true);
    this.addEventListener('shopify:block:select', this.onBlockSelect);
  }

  disconnectedCallback() {
    this.removeEventListener('toggle', this.onToggle, true);
    this.removeEventListener('shopify:block:select', this.onBlockSelect);
  }
}

const lpDefine = (name, constructor) => {
  if (!customElements.get(name)) customElements.define(name, constructor);
};

lpDefine('lp-product-form', LpProductForm);
lpDefine('lp-tabs', LpTabs);
lpDefine('lp-carousel', LpCarousel);
lpDefine('lp-counter', LpCounter);
lpDefine('lp-marquee', LpMarquee);
lpDefine('lp-modal-opener', LpModalOpener);
lpDefine('lp-accordion-group', LpAccordionGroup);
lpDefine('lp-sticky-bar', LpStickyBar);

/* ------------------------------------------------------------------------ */
/* Infaden bij scrollen                                                     */
/* ------------------------------------------------------------------------ */

const lpRevealObserver =
  'IntersectionObserver' in window
    ? new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            if (!entry.isIntersecting) return;
            entry.target.classList.add('is-visible');
            lpRevealObserver.unobserve(entry.target);
          });
        },
        { rootMargin: '0px 0px -6% 0px', threshold: 0.05 }
      )
    : null;

function lpScanReveal(root = document) {
  const viewportHeight = window.innerHeight || document.documentElement.clientHeight;
  root.querySelectorAll('.lp-animate:not(.is-visible)').forEach((element) => {
    const rect = element.getBoundingClientRect();
    const inView = rect.top < viewportHeight && rect.bottom > 0;
    if (!lpRevealObserver || lpIsDesignMode() || inView) {
      element.classList.add('is-visible');
    } else {
      lpRevealObserver.observe(element);
    }
  });
}

lpScanReveal();
document.documentElement.classList.add('lp-js');
document.addEventListener('shopify:section:load', (event) => lpScanReveal(event.target));
